import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import WhoWeAreSection from "./WhoWeAreSection";
import MusicCommunityNode from "./MusicCommunityNode";
import { Hoard } from "./Hoard";
import { OPERATOR_NAME, CONTACT_EMAIL } from "../config/operator";
import { EmailUsButton } from "./EmailUsButton";

const Footer = ({ showReferralForm = false }) => {
  const [referralOpen, setReferralOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [referralData, setReferralData] = useState({
    org_name: "",
    state: "",
    website: "",
    contact_name: "",
    contact_email: "",
    additional_info: ""
  });

  // Hide footer when page is embedded inside an iframe (e.g. /SoundLifeHub)
  if (typeof window !== "undefined" && window.location.search.includes("embed=1")) {
    return null;
  }

  const handleReferralSubmit = async (e) => {
    e.preventDefault();
    if (!referralData.org_name || !referralData.additional_info) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, referralData, { withCredentials: true });
      toast.success("Thank you! Your referral has been submitted for review.");
      setReferralOpen(false);
      setReferralData({
        org_name: "",
        state: "",
        website: "",
        contact_name: "",
        contact_email: "",
        additional_info: ""
      });
    } catch (error) {
      toast.error("Failed to submit referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1A1A1A] text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-center text-sm md:text-base text-white/85 leading-relaxed mb-10 pb-8 border-b border-white/10" data-testid="footer-built-by-line">
          THE WALL is 100% Concepted, Designed &amp; Built by <span className="font-semibold text-white">Artists 4 Life in the Sound</span> elementally, developed PUBLICLY since 2012 —{" "}
          <Link to="/our-origins" className="font-bold uppercase tracking-wider text-[#E76F51] hover:text-[#f08a70] underline underline-offset-4" data-testid="footer-origins-link">see our ORIGINS</Link>
        </p>
        <div className="grid md:grid-cols-5 gap-8 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="h-8 w-8 text-[#E76F51]" fill="#E76F51" />
              <span className="font-['Playfair_Display'] text-2xl font-semibold">THE WALL</span>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              THE WALL builds hope in community in collaboration with Stars 4 Peace. Local organizations gain support they need and our communities grow stronger as people provide the resources they have to make kindness common sense.
            </p>
            <p className="text-white/70 leading-relaxed mb-2 max-w-md">
              We are Artists 4 Life in the Sound.
            </p>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              This is the Power of Peace to Provide.
            </p>
            
            {/* Referral Section */}
            {showReferralForm ? (
              <Dialog open={referralOpen} onOpenChange={setReferralOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-[#E76F51] hover:bg-[#E76F51]/90 rounded-full"
                    data-testid="refer-org-btn"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Refer an Organization
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-['Playfair_Display'] text-2xl">Refer an Organization</DialogTitle>
                    <DialogDescription>
                      Know a 501(c)3 organization that should be included? Let us know!
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleReferralSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="org_name">Organization Name *</Label>
                      <Input
                        id="org_name"
                        value={referralData.org_name}
                        onChange={(e) => setReferralData({...referralData, org_name: e.target.value})}
                        placeholder="Organization name"
                        className="mt-1"
                        data-testid="referral-org-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="state">State of Operations</Label>
                        <Input
                          id="state"
                          value={referralData.state}
                          onChange={(e) => setReferralData({...referralData, state: e.target.value})}
                          placeholder="e.g., Washington"
                          className="mt-1"
                          data-testid="referral-state"
                        />
                      </div>
                      <div>
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          value={referralData.website}
                          onChange={(e) => setReferralData({...referralData, website: e.target.value})}
                          placeholder="https://..."
                          className="mt-1"
                          data-testid="referral-website"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_name">Point of Contact</Label>
                        <Input
                          id="contact_name"
                          value={referralData.contact_name}
                          onChange={(e) => setReferralData({...referralData, contact_name: e.target.value})}
                          placeholder="Contact name"
                          className="mt-1"
                          data-testid="referral-contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Contact Email</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={referralData.contact_email}
                          onChange={(e) => setReferralData({...referralData, contact_email: e.target.value})}
                          placeholder="email@example.com"
                          className="mt-1"
                          data-testid="referral-contact-email"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="additional_info">What does this organization do that you value? What experience have you had with them? *</Label>
                      <Textarea
                        id="additional_info"
                        value={referralData.additional_info}
                        onChange={(e) => setReferralData({...referralData, additional_info: e.target.value})}
                        placeholder="Share your experience and why this organization matters..."
                        className="mt-1 min-h-[100px]"
                        data-testid="referral-additional-info"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full"
                      disabled={submitting}
                      data-testid="submit-referral-btn"
                    >
                      {submitting ? "Submitting..." : "Submit Referral"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Link to="/">
                <Button 
                  className="bg-[#E76F51] hover:bg-[#E76F51]/90 rounded-full"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Refer an Organization
                </Button>
              </Link>
            )}
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-2 text-white/70">
              <li><Link to="/how-it-works" className="hover:text-[#E76F51] transition-colors" data-testid="footer-how-link">How it works · 1-2-3</Link></li>
              <li><Link to="/news" className="hover:text-[#E76F51] transition-colors" data-testid="footer-news-link">News · Range Free Radio</Link></li>
              <li><Link to="/#causes" className="hover:text-[#E76F51] transition-colors">Causes</Link></li>
              <li><Link to="/trove" className="hover:text-[#E76F51] transition-colors">The <Hoard /></Link></li>
              <li><Link to="/shop" className="hover:text-[#E76F51] transition-colors" data-testid="footer-shop-link">The Giving Shop</Link></li>
              <li><Link to="/dashboard" className="hover:text-[#E76F51] transition-colors">Contribute</Link></li>
              <li><Link to="/dashboard" className="hover:text-[#E76F51] transition-colors text-lg font-semibold">BANDS: Add YOUR music to THE WALL</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-white">About</h4>
            <ul className="space-y-2 text-white/70">
              <li><Link to="/safety" className="hover:text-[#E76F51] transition-colors" data-testid="footer-safety-link">Safety · North Star Guide</Link></li>
              <li><Link to="/safety/testing" className="hover:text-[#E76F51] transition-colors" data-testid="footer-safety-testing-link">Safety · Testing</Link></li>
              <li><Link to="/origins" className="hover:text-[#E76F51] transition-colors" data-testid="footer-origins-link">Origins · The Long Story</Link></li>
              <li><Link to="/musicians" className="hover:text-[#E76F51] transition-colors" data-testid="footer-musicians-link">Musicians · One-Stop Page</Link></li>
              <li><Link to="/for-bands" className="hover:text-[#E76F51] transition-colors" data-testid="footer-for-bands-link">For Bands · How to Join</Link></li>
              <li><a href="https://www.stars4peace.org/" target="_blank" rel="noopener noreferrer" className="hover:text-[#E76F51] transition-colors">Stars 4 Peace</a></li>
              <li><a href="https://www.stars4peace.org/history.html#/" target="_blank" rel="noopener noreferrer" className="hover:text-[#E76F51] transition-colors">The Wall</a></li>
              <li><Link to="/contact" className="hover:text-[#E76F51] transition-colors" data-testid="footer-contact-link">Contact</Link></li>
              <li className="pt-1"><EmailUsButton variant="dark" testId="footer-email-us" /></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-white">Legal</h4>
            <ul className="space-y-2 text-white/70">
              <li><Link to="/privacy" className="hover:text-[#E76F51] transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-[#E76F51] transition-colors">Terms of Service</Link></li>
              <li><Link to="/disclaimer" className="hover:text-[#E76F51] transition-colors" data-testid="footer-disclaimer-link">Medical & Safety Disclaimer</Link></li>
              <li><Link to="/copyright" className="hover:text-[#E76F51] transition-colors" data-testid="footer-copyright-link">Copyright & Takedown</Link></li>
              <li><Link to="/ai" className="hover:text-[#E76F51] transition-colors" data-testid="footer-ai-link">How we use AI</Link></li>
              <li><Link to="/contact" className="hover:text-[#E76F51] transition-colors">Contact: {CONTACT_EMAIL}</Link></li>
              <li className="text-xs text-white/60 leading-snug pt-1">The North Star Guide and symptom checker are educational only and do not diagnose. Only a lab test ordered by a clinician can confirm exposure.</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center">
          {/* WHO WE ARE + Music ↔ Community — copied from /bands and /SoundLife
              respectively so the brand story and the community-flywheel cards
              sit just above "THE WALL: DRIVEN BY SOUND" on every page that
              ships the global footer. Both are extracted into reusable
              components so any future edit propagates everywhere they appear. */}
          <div className="text-left mb-10" data-testid="footer-who-we-are-wrap">
            <WhoWeAreSection context="footer" />
          </div>
          <div className="text-left mb-10" data-testid="footer-music-community-wrap">
            <MusicCommunityNode context="footer" />
          </div>

          {/* THE WALL Info Section */}
          <div className="mb-8">
            <p className="text-lg font-semibold text-[#E76F51] mb-1 tracking-wide">
              THE WALL: DRIVEN BY SOUND
            </p>
            <a 
              href="https://lookwashington.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-base font-medium text-white/80 hover:text-[#E76F51] underline transition-colors mb-2 block"
            >
              Online at LOOKWASHINGTON.COM
            </a>
            <span className="text-sm font-medium tracking-wider uppercase text-white/70 block">
              WITH STARS 4 PEACE
            </span>
            <p className="text-base text-white/70 mb-1">
              In concert with Artists 4 Life in the Sound.
            </p>
            <p className="text-xl font-medium text-[#E76F51] mb-1">
              This is the Power of Peace to Provide.
            </p>
            <p className="text-base text-white/70 mb-4">
              Created at the Heart of Washington State, in the City of Destiny
            </p>
            <div className="flex justify-center gap-4">
              <a 
                href="https://www.stars4peace.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-white/80 hover:text-[#E76F51] underline transition-colors"
              >
                Learn More about Stars 4 Peace
              </a>
              <a 
                href="https://www.stars4peace.org/us.html#/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-white/80 hover:text-[#E76F51] underline transition-colors"
              >
                Learn More about Artists 4 Life in the Sound
              </a>
            </div>
          </div>
          
          {/* Artists 4 Life in the Sound Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_7f85c025-5a2b-45a1-a9fb-658f70cc6e65/artifacts/1q8a85qu_A4S.Logo.MUSIC%20AND%20HEART.jpg"
            alt="Artists 4 Life in the Sound"
            className="w-full max-w-4xl mx-auto mb-8"
          />
          {/* Tacoma.Rocks teapot — smaller circular mark above the copyright */}
          <img
            src="https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/68jxcbi5_a4s-2015-jive-logo-png_orig.png"
            alt="Tacoma.Rocks · Artists 4 Life in the Sound"
            className="mx-auto h-24 w-24 sm:h-28 sm:w-28 rounded-full mb-6"
            data-testid="footer-tacoma-rocks-logo"
          />
          <p className="text-white/50 text-sm">&copy; 2026 THE WALL by {OPERATOR_NAME}, in collaboration with Stars 4 Peace. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
